/**
 * Context React qui porte L'ÉTAT unique d'une chasse aux indices dans le
 * player. Deux consommateurs :
 *   - `ClueHuntCasesInline` : rendu INLINE des cases indices dans le flow du
 *     bloc `clue_hunt` (comme un statement normal, pas caché dans le sticky).
 *   - `ClueHuntStickyBar` : barre sticky bottom minimaliste (juste texte
 *     + chrono, PAS les cases).
 *   - `CluePopup` : toast bottom absolu quand un indice est trouvé/révélé.
 *
 * Une seule source de vérité pour le compteur de clics, le chrono, les
 * indices révélés, la XP totale.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  applyClueFound,
  applyTimeout,
  applyWrongClick,
  CLUE_HUNT_DEFAULT_TIMEOUT_MS,
  findClueByClick,
  initialClueHuntState,
  isClueHuntDone,
  totalEarned,
  type Clue,
  type ClueHuntData,
  type ClueHuntState,
} from '@jeraisonne/db';
import { useClueOverlay } from './use-clue-overlay';

export interface CluePopupState {
  key: string;
  revealed_md: string;
  xp: number;
}

/** Floating badge « +N XP » qui surgit au point du clic. */
export interface FloatingXpState {
  key: string;
  x: number;
  y: number;
  xp: number;
}

interface ClueHuntContextValue {
  data: ClueHuntData;
  state: ClueHuntState;
  chronoStartedAt: number;
  timeoutMs: number;
  now: number;
  done: boolean;
  popup: CluePopupState | null;
  dismissPopup: () => void;
  floatingXp: FloatingXpState | null;
  dismissFloatingXp: () => void;
}

const Ctx = createContext<ClueHuntContextValue | null>(null);

export function useClueHuntCtx(): ClueHuntContextValue | null {
  return useContext(Ctx);
}

export interface ClueHuntProviderProps {
  data: ClueHuntData;
  /** Ref du wrapper "scope" — l'overlay écoute les clics dedans. */
  scopeRef: React.RefObject<HTMLElement | null>;
  /** Appelé UNE fois quand tous les indices sont trouvés. */
  /** State final passé à onCompleted — permet au parent de conserver les
      indices révélés/leur XP après démontage du provider. */
  onCompleted: (final: {
    totalXp: number;
    revealed: string[];
    earned: Array<[string, number]>;
  }) => void;
  children: ReactNode;
}

export function ClueHuntProvider({ data, scopeRef, onCompleted, children }: ClueHuntProviderProps) {
  const clues = data.clues;
  const timeoutMs = data.timeout_ms || CLUE_HUNT_DEFAULT_TIMEOUT_MS;

  const [state, setState] = useState<ClueHuntState>(() => initialClueHuntState());
  const [popup, setPopup] = useState<CluePopupState | null>(null);
  const [floatingXp, setFloatingXp] = useState<FloatingXpState | null>(null);
  const [chronoStartedAt, setChronoStartedAt] = useState<number>(() => Date.now());
  const [now, setNow] = useState<number>(() => Date.now());
  // Miroir ref du départ du chrono : lisible sans dépendance dans les callbacks
  // (le bonus de vitesse a besoin du temps écoulé au moment exact du bon clic).
  const chronoStartedAtRef = useRef<number>(chronoStartedAt);
  const resetChrono = useCallback(() => {
    const t = Date.now();
    chronoStartedAtRef.current = t;
    setChronoStartedAt(t);
  }, []);

  const done = useMemo(() => isClueHuntDone(state, clues), [state, clues]);
  const enabled = !done;

  const completedRef = useRef(false);
  useEffect(() => {
    if (!done || completedRef.current) return;
    completedRef.current = true;
    onCompleted({
      totalXp: totalEarned(state),
      revealed: Array.from(state.revealed),
      earned: Array.from(state.earned),
    });
    // Force le dismiss de la popup après done : 800ms pour laisser voir la
    // dernière notif puis on nettoie (safety, au cas où le setTimeout de la
    // popup elle-même serait cassé par un démontage).
    const t = window.setTimeout(() => setPopup(null), 800);
    return () => window.clearTimeout(t);
  }, [done, state, onCompleted]);

  useEffect(() => {
    if (!enabled) return;
    const tick = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(tick);
  }, [enabled, chronoStartedAt]);

  // Timer de chrono (timeout = révélation auto sans XP).
  useEffect(() => {
    if (!enabled) return;
    const t = window.setTimeout(() => {
      setState((prev) => {
        const { state: next, revealed } = applyTimeout(prev, clues);
        if (revealed) {
          setPopup({
            key: `${revealed.id}-${Date.now()}`,
            revealed_md: revealed.revealed_md,
            xp: 0,
          });
        }
        return next;
      });
      resetChrono();
    }, timeoutMs);
    return () => window.clearTimeout(t);
  }, [enabled, chronoStartedAt, timeoutMs, clues, resetChrono]);

  const handleWordClick = useCallback(
    (text: string | null, evt: MouseEvent | TouchEvent) => {
      if (!text) return;
      // Coordonnées viewport du clic (pour le floating XP badge).
      const point =
        'touches' in evt
          ? (evt.changedTouches?.[0] ?? evt.touches?.[0])
          : { clientX: evt.clientX, clientY: evt.clientY };
      const clickX = point?.clientX ?? 0;
      const clickY = point?.clientY ?? 0;

      // Temps écoulé depuis le dernier reset du chrono → bonus de vitesse.
      const elapsedMs = Date.now() - chronoStartedAtRef.current;
      setState((prev) => {
        const clue = findClueByClick(text, clues, prev.revealed);
        if (clue) {
          // Bon clic : révèle, crédite XP (dégressif selon la vitesse), reset
          // chrono, floating badge.
          const next = applyClueFound(prev, clue, elapsedMs, timeoutMs);
          const xp = next.earned.get(clue.id) ?? 0;
          if (xp > 0) {
            setFloatingXp({ key: `${clue.id}-${Date.now()}`, x: clickX, y: clickY, xp });
          }
          // La popup bottom reste en fallback (pour le rappel du texte trouvé).
          setPopup({ key: `${clue.id}-${Date.now()}`, revealed_md: clue.revealed_md, xp });
          resetChrono();
          return next;
        }
        // Mauvais clic : au 3e consécutif → révélation auto sans XP.
        const { state: nextState, revealed } = applyWrongClick(prev, clues);
        if (revealed) {
          setPopup({
            key: `${revealed.id}-${Date.now()}`,
            revealed_md: revealed.revealed_md,
            xp: 0,
          });
          resetChrono();
        }
        return nextState;
      });
    },
    [clues, timeoutMs, resetChrono],
  );

  useClueOverlay({ scopeRef, enabled, onWordClick: handleWordClick });

  const value: ClueHuntContextValue = {
    data,
    state,
    chronoStartedAt,
    timeoutMs,
    now,
    done,
    popup,
    dismissPopup: () => setPopup(null),
    floatingXp,
    dismissFloatingXp: () => setFloatingXp(null),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export type { Clue };
